/**
 * How old a notification is, in the reader's language.
 *
 * Both surfaces — the header dropdown and `/notifications` — used to hard-code
 * "just now" / "2m ago" / "3h ago" in English whatever the interface language
 * was (BUG-1). `Intl.RelativeTimeFormat` already carries the wording for all
 * three locales, so only the sub-minute case needs a translation key: a zero
 * delta renders as "this minute" / "в эту минуту", which is not what a fresh
 * notification should say.
 *
 * `now` is a parameter so the tests do not depend on the wall clock; the
 * locale is passed in rather than read from i18next so the function stays pure.
 */
export function formatNotificationTime(
  iso: string,
  locale: string,
  justNow: string,
  options: { compact?: boolean; now?: number } = {}
): string {
  const date = new Date(iso);
  const time = date.getTime();
  if (Number.isNaN(time)) return "";

  const now = options.now ?? Date.now();
  const diffMin = Math.floor((now - time) / 60000);
  // A timestamp in the future is clock skew, not a countdown.
  if (diffMin < 1) return justNow;

  /**
   * `short`, never `narrow`: CLDR's narrow past form for Russian is a *minus
   * sign* — `-5 мин`, `-3 ч` — so the bell dropdown read like a countdown
   * (BUG-57). Short says «5 мин. назад» and stays shorter than the long form,
   * which is all the dropdown wanted from narrow in the first place.
   */
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "always",
    style: options.compact ? "short" : "long",
  });
  if (diffMin < 60) return rtf.format(-diffMin, "minute");

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return rtf.format(-diffH, "hour");

  return options.compact
    ? date.toLocaleDateString(locale)
    : date.toLocaleString(locale);
}
